// src/pages/PublicCatalog/index.js
import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Badge,
    Container,
    Grid,
    CircularProgress,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Snackbar,
    Fab
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ShoppingCart as ShoppingCartIcon, Search as SearchIcon } from '@material-ui/icons';
import { useStyles } from './styles';
import ProductCard from './components/ProductCard';
import ProductDetails from './components/ProductDetails';
import CartDialog from './components/CartDialog';
import PhoneDialog from './components/PhoneDialog';
import { formatMessage } from './utils';
import { getBackendUrl, getBusinessName, getMessageAPICode } from "../../config";
import toastError from '../../errors/toastError';
import publicApi from '../../services/publicApi';
import ProductPage from './components/ProductPage';

const PublicCatalog = () => {
    const classes = useStyles();

    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [cart, setCart] = useState({});
    const [loading, setLoading] = useState(true);
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('505');
    const [cartOpen, setCartOpen] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [sortBy, setSortBy] = useState('name');
    const [currentImageIndexes, setCurrentImageIndexes] = useState({});
    const [settings, setSettings] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [singleProductView, setSingleProductView] = useState(false);
    const [singleProduct, setSingleProduct] = useState(null);


    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await publicApi.get('/categories');
                setCategories(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchSession = async () => {
            try {
                const { data } = await publicApi.get("/settings");
                setSettings(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchSession();
    }, []);
    const getSettingValue = key => {
        const setting = settings.find(s => s.key === key);
        return setting ? setting.value : "";
    };
    useEffect(() => {
        if (singleProductView && singleProduct) {
            // Check if the single product is already in the products array
            const productExists = products.some(p => p.id === singleProduct.id);

            if (!productExists) {
                // Add the single product to the products array to ensure cart functionality works
                setProducts(prevProducts => [...prevProducts, singleProduct]);
                setFilteredProducts(prevFilteredProducts => [...prevFilteredProducts, singleProduct]);
            }
        }
    }, [singleProductView, singleProduct, products]);
    useEffect(() => {
        let mounted = true;

        // Parse URL parameters
        const params = new URLSearchParams(window.location.search);
        const productId = params.get('productId');
        const categoryId = params.get('categoryId');
        const searchTermParam = params.get('searchTerm');
        const phoneFromUrl = params.get('phoneNumber');

        // Handle phone number
        if (phoneFromUrl && mounted) {
            setPhoneNumber(phoneFromUrl);
            setCountryCode("");
        } else if (mounted && !phoneNumber) {
            setPhoneModalOpen(true);
        }

        // Check if viewing a single product
        if (productId) {
            const loadSingleProduct = async () => {
                try {
                    const { data } = await publicApi.get(`/products/public/${productId}`);
                    if (mounted) {
                        const processedProduct = {
                            ...data,
                            price: typeof data.price === 'string' ? parseFloat(data.price) : data.price
                        };
                        setSingleProduct(processedProduct);
                        setSingleProductView(true);
                        setLoading(false);
                    }
                } catch (err) {
                    if (mounted) {
                        setError('Error al cargar el producto');
                        setLoading(false);
                    }
                }
            };
            loadSingleProduct();
            return () => { mounted = false; };
        }

        // Set filters from URL if provided
        if (categoryId && mounted) {
            setSelectedCategory(categoryId);
        }

        // Only set the search term from URL if it hasn't been set yet
        if (searchTermParam && mounted && searchTerm === '') {
            setSearchTerm(searchTermParam);
        }

        // Load products
        const loadProducts = async () => {
            try {
                const queryParams = {};

                if (selectedCategory) {
                    queryParams.categoryId = selectedCategory;
                }

                if (searchTerm) {
                    // Use the original searchTerm (not lowercase) since the backend
                    // should handle case-insensitivity on its end
                    queryParams.searchParam = searchTerm;
                }

                const { data } = await publicApi.get('/products/public', { params: queryParams });

                if (mounted) {
                    const processedProducts = data.products.map(product => ({
                        ...product,
                        price: typeof product.price === 'string' ? parseFloat(product.price) : product.price
                    }));
                    setProducts(processedProducts);
                    setFilteredProducts(processedProducts);
                    setLoading(false);
                }
            } catch (err) {
                if (mounted) {
                    setError('Error al cargar los productos');
                    setLoading(false);
                }
            }
        };

        loadProducts();

        return () => { mounted = false; };
    }, [selectedCategory, searchTerm]);
    useEffect(() => {
        // Convert search term to lowercase once for efficiency
        const searchTermLower = searchTerm.toLowerCase();

        const filtered = products.filter(product =>
            // Product name search - case insensitive
            (product.name && product.name.toLowerCase().includes(searchTermLower)) ||
            // Description search - case insensitive and check if it exists
            (product.description && product.description.toLowerCase().includes(searchTermLower)) ||
            // Attributes search - case insensitive and check all attribute fields
            (product.attributes && product.attributes.some(attr =>
                (attr.name && attr.name.toLowerCase().includes(searchTermLower)) ||
                (attr.value && attr.value.toLowerCase().includes(searchTermLower))
            ))
        );

        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'price-asc':
                    return a.price - b.price;
                case 'price-desc':
                    return b.price - a.price;
                case 'name':
                default:
                    return a.name.localeCompare(b.name);
            }
        });

        setFilteredProducts(sorted);
    }, [searchTerm, products, sortBy]);

    const handleImageNavigation = (productId, direction) => {
        setCurrentImageIndexes(prev => {
            const currentIndex = prev[productId] || 0;
            const product = products.find(p => p.id === productId);
            const maxIndex = product.images.length - 1;

            if (direction === 'next') {
                return { ...prev, [productId]: Math.min(currentIndex + 1, maxIndex) };
            } else {
                return { ...prev, [productId]: Math.max(currentIndex - 1, 0) };
            }
        });
    };

    const handleAddToCart = (productId, quantity = 1) => {
        // Convert to number to ensure consistent type comparison
        const numericProductId = Number(productId);

        setCart(prev => ({
            ...prev,
            [numericProductId]: (prev[numericProductId] || 0) + quantity
        }));
    };

    const handleUpdateCartQuantity = (productId, change) => {
        setCart(prev => {
            const newCart = { ...prev };
            const newQuantity = (prev[productId] || 0) + change;

            if (newQuantity <= 0) {
                delete newCart[productId];
            } else {
                newCart[productId] = newQuantity;
            }

            return newCart;
        });
    };

    const handleRemoveFromCart = (productId) => {
        setCart(prev => {
            const newCart = { ...prev };
            delete newCart[productId];
            return newCart;
        });
    };

    const handleFinalizePurchase = async () => {
        if (!phoneNumber) {
            setPhoneModalOpen(true);
            return;
        }

        const cartItems = Object.entries(cart).map(([productId, quantity]) => {
            const product = products.find(p => p.id === parseInt(productId));
            return {
                name: product.name,
                price: product.price,
                quantity,
                id: product.id,
                subtotal: product.price * quantity
            };
        });

        const total = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
        const message = formatMessage(cartItems, total);

        try {
            await publicApi.post('/messages/custom', {
                message,
                phoneNumber: `${countryCode}${phoneNumber}`,
                code: getMessageAPICode()
            });

            setCart({});
            setCartOpen(false);
            setSuccessMessage('¡Pedido enviado con éxito! Recibirás un mensaje con los detalles.');
        } catch (err) {
            setError('Error al enviar el pedido');
        }
    };
    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);

        // Update URL with category parameter
        const url = new URL(window.location);
        if (categoryId) {
            url.searchParams.set('categoryId', categoryId);
        } else {
            url.searchParams.delete('categoryId');
        }
        window.history.pushState({}, '', url.toString());
    };

    // Add handler to exit single product view
    const handleExitSingleProductView = () => {
        setSingleProductView(false);
        setSingleProduct(null);
        setLoading(true); // Show loading while products are being fetched

        // Update URL to remove productId parameter
        const url = new URL(window.location);
        url.searchParams.delete('productId');
        window.history.pushState({}, '', url.toString());

        // Reload products
        const loadProducts = async () => {
            try {
                const queryParams = {};

                if (selectedCategory) {
                    queryParams.categoryId = selectedCategory;
                }

                if (searchTerm) {
                    queryParams.searchParam = searchTerm;
                }

                const { data } = await publicApi.get('/products/public', { params: queryParams });

                const processedProducts = data.products.map(product => ({
                    ...product,
                    price: typeof product.price === 'string' ? parseFloat(product.price) : product.price
                }));

                setProducts(processedProducts);
                setFilteredProducts(processedProducts);
                setLoading(false);
            } catch (err) {
                setError('Error al cargar los productos');
                setLoading(false);
            }
        };

        loadProducts();
    };


    // Add function to generate shareable product link
    const getProductShareableLink = (productId) => {
        const baseUrl = window.location.origin + window.location.pathname;
        const phoneParam = phoneNumber ? `&phoneNumber=${phoneNumber}` : '';
        return `${baseUrl}?productId=${productId}${phoneParam}`;
    };

    if (loading) {
        return (
            <div className={classes.loading}>
                <CircularProgress />
            </div>
        );
    }
    if (getSettingValue("storeEnabled") !== "enabled") {
        return (
            <div className={classes.loading}>
                <Typography variant="h6">La tienda está deshabilitada</Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <AppBar position="sticky" className={classes.appBar}>
                <Toolbar>
                    <div className={classes.toolbar}>
                        <Typography variant="h5" className={classes.title}>
                            {getSettingValue("storeName")}
                        </Typography>
                        <IconButton color="inherit" onClick={() => setCartOpen(true)}>
                            <Badge badgeContent={Object.values(cart).reduce((a, b) => a + b, 0)} color="secondary">
                                <ShoppingCartIcon />
                            </Badge>
                        </IconButton>
                    </div>
                </Toolbar>
            </AppBar>

            <div className={classes.searchBar}>
                <Container>
                    <div className={classes.searchContainer}>
                        <TextField
                            className={classes.searchInput}
                            placeholder="Buscar por nombre, descripción o características..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => {
                                const newSearchTerm = e.target.value;
                                setSearchTerm(newSearchTerm);

                                // Update URL with search parameter
                                const url = new URL(window.location);
                                if (newSearchTerm) {
                                    url.searchParams.set('searchTerm', newSearchTerm);
                                } else {
                                    url.searchParams.delete('searchTerm');
                                }
                                window.history.pushState({}, '', url.toString());
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                            <InputLabel>Categoría</InputLabel>
                            <Select
                                value={selectedCategory}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                label="Categoría"
                            >
                                <MenuItem value="">Todas las categorías</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                            <InputLabel>Ordenar por</InputLabel>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                label="Ordenar por"
                            >
                                <MenuItem value="name">Nombre</MenuItem>
                                <MenuItem value="price-asc">Menor precio</MenuItem>
                                <MenuItem value="price-desc">Mayor precio</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </Container>
            </div>

            <Container className={classes.container}>
                {error && (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        {error}
                    </Alert>
                )}

                {singleProductView && singleProduct ? (
                    // Single product view
                    <ProductPage
                        product={singleProduct}
                        onAddToCart={handleAddToCart}
                        onBack={handleExitSingleProductView}
                        shareableLink={getProductShareableLink(singleProduct.id)}
                    />
                ) : (
                    // Regular product grid
                    <>
                        {filteredProducts.length === 0 ? (
                            <Paper className={classes.noProducts}>
                                <Typography variant="h6">
                                    No se encontraron productos
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Intenta con otros términos de búsqueda
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={3}>
                                {filteredProducts.map(product => (
                                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                                        <ProductCard
                                            product={product}
                                            currentImageIndex={currentImageIndexes[product.id] || 0}
                                            onImageNavigate={handleImageNavigation}
                                            onShowDetails={() => setSelectedProduct(product)}
                                            cartQuantity={cart[product.id] || 0}
                                            onAddToCart={handleAddToCart}
                                            onRemoveFromCart={handleRemoveFromCart}
                                            onViewProduct={() => {
                                                // Navigate to single product view
                                                window.location.href = `?productId=${product.id}${phoneNumber ? `&phoneNumber=${phoneNumber}` : ''}`;
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Container>

            <ProductDetails
                open={!!selectedProduct}
                onClose={() => setSelectedProduct(null)}
                product={selectedProduct}
                onAddToCart={handleAddToCart}
            />

            <CartDialog
                open={cartOpen}
                onClose={() => setCartOpen(false)}
                cart={cart}
                products={products}
                onUpdateQuantity={handleUpdateCartQuantity}
                onRemoveItem={handleRemoveFromCart}
                onFinalize={handleFinalizePurchase}
            />

            <PhoneDialog
                open={phoneModalOpen}
                onClose={() => setPhoneModalOpen(false)}
                phoneNumber={phoneNumber}
                onPhoneChange={(value) => setPhoneNumber(value)}
                countryCode={countryCode}
                onCountryCodeChange={(value) => setCountryCode(value)}
            />

            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={() => setSuccessMessage('')}
            >
                <Alert onClose={() => setSuccessMessage('')} severity="success">
                    {successMessage}
                </Alert>
            </Snackbar>
            <Fab
                color="primary"
                aria-label="cart"
                className={classes.floatingCartButton}
                onClick={() => setCartOpen(true)}
            >
                <Badge badgeContent={Object.values(cart).reduce((a, b) => a + b, 0)} color="secondary">
                    <ShoppingCartIcon />
                </Badge>
            </Fab>
        </div>
    );
};


export default PublicCatalog;